/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @version 1.25.0
 * @since 1.12.0
 */

import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { Middleware } from 'redux';
import { SagaMiddleware } from 'redux-saga';

import { pageApi } from './api/page.api';
import { routerMiddleware, routesInterceptorMiddleware, sagaMiddleware } from './middlewares';
import { inquiryApi } from './modules/inquiry/inquiryApi';
import { tableListenerMiddleware } from './modules/table';
import { tableApi } from './modules/table/store';
import { tableOrderListenerMiddleware } from './pages/PageHistoryOrder';
import { tableSubscriberListenerMiddleware } from './pages/PageSubscriberMobile';
import { createReducer } from './reducers/rootReducer';
import rootSaga from './sagas/rootSaga';
import type { TStore } from './types/globalTypes';

const middleware: Array<Middleware | SagaMiddleware<object>> = [
    // uncomment this middleware, if you want to see all events dispatch in browser console
    // loggerMiddleware,
    routerMiddleware,
    routesInterceptorMiddleware,
    sagaMiddleware,
    pageApi.middleware,
    tableApi.middleware,
    inquiryApi.middleware,
    tableListenerMiddleware.middleware,
    tableOrderListenerMiddleware.middleware,
    tableSubscriberListenerMiddleware.middleware,
];

const createStore = (initialState?: object) => {
    const store: TStore = configureStore({
        reducer: createReducer(),
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: false,
            }).concat(...middleware),
        preloadedState: initialState,
    });
    store.asyncReducers = {};

    store.injectReducer = (key, asyncReducer) => {
        if (store.asyncReducers) {
            store.asyncReducers[key] = asyncReducer;
            store.replaceReducer(createReducer(store.asyncReducers));
        }
    };

    return store;
};

export const globalAppStore: EnhancedStore = createStore();

sagaMiddleware.run(rootSaga);

setupListeners(globalAppStore.dispatch);

export const testGlobalAppStore = (testInitialState?: object) => {
    const store = createStore(testInitialState);

    sagaMiddleware.run(rootSaga);
    setupListeners(store.dispatch);

    return store;
};
